import React, { useContext, useMemo } from 'react';
import { ChartContainer } from './PPAInsightPieCharts.style';
import { GlassChart } from '@sede-enterprise/glass-design-library';
import { AppContext } from '../../../../../../Context/AppContext';
import { NCF_DASHBOARD_QTY_DECIMAL_PLACES } from '../../../../../../api/constants';

// Common component that accepts tradeName as a prop
type PptInScopeNcfCatgChartProps = {
  tradeName: string;
};

export const PptInScopeNcfCatgChart: React.FC<PptInScopeNcfCatgChartProps> = ({ tradeName }) => {
  const { state: pptState } = useContext(AppContext).pptReports;
  const chartData = useMemo(() => {
    if (!pptState?.pptNcfCategories || pptState.pptNcfCategories.length === 0) {
      return [];
    }

    const filteredData = pptState.pptNcfCategories.filter(
      (category) => category.tradeName === tradeName,
    );
    const total = filteredData.reduce((sum, category) => sum + category.quantity, 0);
    return filteredData
      .reduce((acc, category) => {
        const existing = acc.find((item) => item.name === category.ncfCategory);
        if (existing) {
          existing.y += category.quantity; // Sum up the quantity for duplicate keys
        } else {
          acc.push({ name: category.ncfCategory, y: category.quantity });
        }
        return acc;
      }, [] as { name: string; y: number }[])
      .map((item) => {
        return {
          ...item,
          item_percentage: ((item.y / total) * 100).toFixed(NCF_DASHBOARD_QTY_DECIMAL_PLACES), // Calculate percentage, 3 decimal rounded
        };
      });
  }, [pptState?.pptNcfCategories, tradeName]);

  const options = useMemo(
    () => ({
      title: {
        text: `${tradeName} - In Scope NCF Category`,
        align: 'left',
      },
      subtitle: {
        text: `An overview of In Scope NCF Category for ${tradeName}`,
        align: 'left',
      },
      chart: {
        type: 'pie',
      },
      accessibility: {
        point: {
          valueSuffix: '%',
        },
      },
      tooltip: {
        pointFormat: '{series.name}: <b>{point.y:.3f}({point.item_percentage:.3f}%)</b>',
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
            format:
              '<span style="font-size: 0.9em"><b>{point.name}</b>' +
              '</span><br>' +
              '<span style="opacity: 0.6">{point.item_percentage:.3f} ' +
              '%</span>',

            connectorColor: 'rgba(128,128,128,0.5)',
          },
        },
      },
      series: [
        {
          name: 'NCF Category',
          colorByPoint: true,
          data: chartData, // Assuming chartData is not used or is an initial setup
        },
      ],
    }),
    [chartData, tradeName], // Dependencies on chartData and tradeName
  );

  return (
    <ChartContainer>
      {chartData.length > 0 ? (
        <>
          <GlassChart options={options} />
          <div
            style={{ display: 'none' }}
            data-testid='report-breakdown-inScopeNcfCategory-chart-hidden-for-automation'
          >
            {JSON.stringify(options.series)}
          </div>
        </>
      ) : (
        <div>No data available for {tradeName}</div>
      )}
    </ChartContainer>
  );
};
